import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  Building2,
  Check,
  CreditCard,
  Link2,
  Loader2,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { SkeletonList } from '@/components/skeleton';
import { useAppConfig } from '@/lib/config';
import {
  useCreateSetupIntent,
  useDeletePaymentMethod,
  usePaymentMethods,
  useSavePaymentMethod,
  useSetDefaultPaymentMethod,
} from '@/lib/payment-methods';
import {
  useEnsureStripeCustomer,
  useLinkStripeCustomer,
  useSearchStripeCustomers,
  useStripeCustomer,
  useUnlinkStripeCustomer,
} from '@/lib/stripe-customer';
import type { PaymentMethodData, StripeCustomerData } from '@/types/api';

const stripePromiseCache: Record<string, Promise<Stripe | null>> = {};

function getStripe(pk: string): Promise<Stripe | null> {
  if (!stripePromiseCache[pk]) {
    stripePromiseCache[pk] = loadStripe(pk);
  }
  return stripePromiseCache[pk];
}

const BRAND_LABEL: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
  unknown: 'Card',
};

export function ClientBilling({ clientId }: { clientId: number }) {
  const { data: methods, isLoading } = usePaymentMethods(clientId);
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className="space-y-4">
      <StripeCustomerCard clientId={clientId} />

      <Card>
        <CardHeader className="flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold">Payment methods</h3>
            <p className="mt-0.5 text-xs text-[var(--color-foreground-muted)]">
              Cards stored at Stripe. Future invoices charge the default card.
            </p>
          </div>
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="size-4" /> Add card
          </Button>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <SkeletonList rows={2} />
          ) : !methods || methods.length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="No cards on file"
              description="Add a card so subscription invoices charge automatically."
              action={
                <Button onClick={() => setAddOpen(true)}>
                  <Plus className="size-4" /> Add card
                </Button>
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {methods.map((m) => (
                <PaymentMethodRow key={m.id} clientId={clientId} method={m} />
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <AddCardDialog
        open={addOpen}
        clientId={clientId}
        onClose={() => setAddOpen(false)}
      />
    </div>
  );
}

function StripeCustomerCard({ clientId }: { clientId: number }) {
  const { data: customer, isLoading } = useStripeCustomer(clientId);
  const unlink = useUnlinkStripeCustomer(clientId);
  const ensure = useEnsureStripeCustomer(clientId);
  const confirm = useConfirm();
  const toast = useToast();
  const [linkOpen, setLinkOpen] = useState(false);

  async function onCreate() {
    try {
      const created = await ensure.mutateAsync();
      toast.success('Stripe customer created', created.name ?? created.id);
    } catch (err) {
      const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
        ?.response?.data;
      toast.error(
        'Could not create customer',
        data?.errors?.stripe_customer?.[0] ?? data?.message ?? 'Check your Stripe configuration and try again.',
      );
    }
  }

  async function onUnlink() {
    const ok = await confirm({
      title: 'Unlink this Stripe customer?',
      body: "This disconnects the client from the Stripe customer. Nothing is deleted in Stripe, but saved cards won't show here until you relink.",
      confirmLabel: 'Unlink',
      tone: 'danger',
    });
    if (!ok) return;
    await unlink.mutateAsync();
    toast.success('Stripe customer unlinked');
  }

  return (
    <Card>
      <CardHeader className="flex items-start justify-between">
        <div>
          <h3 className="text-sm font-semibold">Stripe customer</h3>
          <p className="mt-0.5 text-xs text-[var(--color-foreground-muted)]">
            The Stripe customer this client's cards and charges belong to.
          </p>
        </div>
        {!isLoading && !customer ? (
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => setLinkOpen(true)}>
              <Link2 className="size-4" /> Link customer
            </Button>
            <Button onClick={onCreate} disabled={ensure.isPending}>
              <Plus className="size-4" /> {ensure.isPending ? 'Creating…' : 'Create customer'}
            </Button>
          </div>
        ) : null}
      </CardHeader>
      <CardBody>
        {isLoading ? (
          <SkeletonList rows={1} />
        ) : customer ? (
          <div className="flex items-center gap-3 text-sm">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-muted)]">
              <Building2 className="size-4 text-[var(--color-foreground-muted)]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">
                {customer.name ?? customer.email ?? customer.id}
              </div>
              <div className="truncate text-xs text-[var(--color-foreground-muted)]">
                {customer.email ? `${customer.email} · ` : ''}
                <span className="font-mono">{customer.id}</span>
              </div>
            </div>
            <Button variant="secondary" onClick={onUnlink} disabled={unlink.isPending}>
              Unlink
            </Button>
          </div>
        ) : (
          <p className="text-sm text-[var(--color-foreground-muted)]">
            Not linked yet. Create a new Stripe customer, link an existing one, or one is created
            automatically the first time you add a card.
          </p>
        )}
      </CardBody>

      <LinkStripeCustomerDialog
        clientId={clientId}
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
      />
    </Card>
  );
}

function LinkStripeCustomerDialog({
  clientId,
  open,
  onClose,
}: {
  clientId: number;
  open: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebouncedValue(query, 300);
  const link = useLinkStripeCustomer(clientId);
  const toast = useToast();

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const results = useSearchStripeCustomers(clientId, debounced, open);
  const looksLikeId = /^cus_[A-Za-z0-9]+$/.test(query.trim());

  async function doLink(customerId: string) {
    try {
      await link.mutateAsync(customerId);
      toast.success('Stripe customer linked');
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
          ?.response?.data;
      toast.error(
        'Could not link',
        message?.errors?.customer_id?.[0] ?? message?.message ?? 'Try a different customer.',
      );
    }
  }

  const rows = results.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} title="Link a Stripe customer" size="md">
      <div className="space-y-3">
        <Input
          placeholder="Search by name or email, or paste a cus_… id"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />

        {looksLikeId ? (
          <button
            type="button"
            onClick={() => doLink(query.trim())}
            disabled={link.isPending}
            className="flex w-full items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-left text-sm transition-colors hover:border-[var(--color-primary)] disabled:opacity-50"
          >
            <Link2 className="size-4 text-[var(--color-foreground-muted)]" />
            <span>
              Link <span className="font-mono">{query.trim()}</span> directly
            </span>
          </button>
        ) : null}

        {results.isFetching ? (
          <p className="flex items-center gap-2 py-2 text-sm text-[var(--color-foreground-muted)]">
            <Loader2 className="size-3.5 animate-spin" /> Searching Stripe…
          </p>
        ) : rows.length === 0 && !looksLikeId ? (
          <p className="py-2 text-sm text-[var(--color-foreground-muted)]">
            {debounced
              ? 'No matching customers in Stripe.'
              : 'No customers match this client’s email. Search by name, or paste a cus_… id.'}
          </p>
        ) : (
          <ul className="max-h-72 space-y-1 overflow-auto">
            {rows.map((c) => (
              <StripeCandidateRow
                key={c.id}
                candidate={c}
                pending={link.isPending}
                onLink={() => doLink(c.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
}

function StripeCandidateRow({
  candidate,
  pending,
  onLink,
}: {
  candidate: StripeCustomerData;
  pending: boolean;
  onLink: () => void;
}) {
  const takenByOther = candidate.linked_client_id != null;

  return (
    <li>
      <button
        type="button"
        onClick={onLink}
        disabled={pending || takenByOther}
        className="flex w-full items-center gap-3 rounded-md border border-[var(--color-border)] px-3 py-2 text-left text-sm transition-colors hover:border-[var(--color-primary)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-[var(--color-border)]"
      >
        <Building2 className="size-4 shrink-0 text-[var(--color-foreground-muted)]" />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium">{candidate.name ?? candidate.email ?? candidate.id}</div>
          <div className="truncate text-xs text-[var(--color-foreground-muted)]">
            {candidate.email ? `${candidate.email} · ` : ''}
            <span className="font-mono">{candidate.id}</span>
          </div>
        </div>
        {takenByOther ? (
          <Badge tone="neutral">Linked to {candidate.linked_client_name ?? 'another client'}</Badge>
        ) : (
          <Link2 className="size-4 shrink-0 text-[var(--color-foreground-muted)]" />
        )}
      </button>
    </li>
  );
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

function PaymentMethodRow({
  clientId,
  method,
}: {
  clientId: number;
  method: PaymentMethodData;
}) {
  const setDefault = useSetDefaultPaymentMethod(clientId);
  const del = useDeletePaymentMethod(clientId);
  const confirm = useConfirm();
  const toast = useToast();

  const brandLabel = method.brand ? BRAND_LABEL[method.brand] ?? method.brand : 'Card';

  return (
    <li className="flex items-center gap-3 py-3 text-sm">
      <CreditCard className="size-5 text-[var(--color-foreground-muted)]" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">
            {brandLabel} ending {method.last4 ?? '••••'}
          </span>
          {method.is_default ? (
            <Badge tone="success">
              <Star className="mr-1 size-3" /> Default
            </Badge>
          ) : null}
        </div>
        <div className="text-xs text-[var(--color-foreground-muted)]">
          Exp {method.exp_month?.toString().padStart(2, '0')}/{method.exp_year}
          {method.billing_name ? ` · ${method.billing_name}` : ''}
        </div>
      </div>
      <Menu
        items={[
          {
            key: 'default',
            label: 'Set as default',
            icon: Check,
            disabled: method.is_default,
            onSelect: async () => {
              await setDefault.mutateAsync(method.id);
              toast.success('Default card updated');
            },
          },
          {
            key: 'remove',
            label: 'Remove',
            icon: Trash2,
            danger: true,
            onSelect: async () => {
              const ok = await confirm({
                title: `Remove ${brandLabel} ending ${method.last4}?`,
                body: 'This card will be detached from the client. Future invoices won\'t charge it.',
                confirmLabel: 'Remove card',
                tone: 'danger',
              });
              if (ok) {
                await del.mutateAsync(method.id);
                toast.success('Card removed');
              }
            },
          },
        ]}
      />
    </li>
  );
}

function AddCardDialog({
  open,
  clientId,
  onClose,
}: {
  open: boolean;
  clientId: number;
  onClose: () => void;
}) {
  const config = useAppConfig();
  const setup = useCreateSetupIntent(clientId);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setError(null);
      return;
    }
    setup.mutateAsync()
      .then((res) => setClientSecret(res.client_secret))
      .catch(() => setError('Could not start the card setup. Try again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const stripePromise = useMemo(
    () => (config.data?.stripe_publishable_key
      ? getStripe(config.data.stripe_publishable_key)
      : null),
    [config.data?.stripe_publishable_key],
  );

  return (
    <Dialog open={open} onClose={onClose} title="Add a card" size="md">
      {error ? (
        <p className="text-sm text-[var(--color-danger)]">{error}</p>
      ) : !config.data?.stripe_publishable_key ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">
          Stripe isn't configured yet. Set STRIPE_KEY / STRIPE_SECRET in the backend and try again.
        </p>
      ) : !clientSecret || !stripePromise ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Preparing secure form…</p>
      ) : (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: 'stripe',
            },
          }}
        >
          <CardForm clientId={clientId} onDone={onClose} />
        </Elements>
      )}
    </Dialog>
  );
}

function CardForm({ clientId, onDone }: { clientId: number; onDone: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const save = useSavePaymentMethod(clientId);
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [setDefault, setSetDefault] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message ?? 'Card could not be saved.');
      setSubmitting(false);
      return;
    }

    const pmId =
      typeof setupIntent?.payment_method === 'string'
        ? setupIntent.payment_method
        : setupIntent?.payment_method?.id;

    if (!pmId) {
      setError('Stripe did not return a payment method. Try again.');
      setSubmitting(false);
      return;
    }

    try {
      await save.mutateAsync({ payment_method_id: pmId, set_default: setDefault });
      toast.success('Card saved');
      onDone();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not save the card on the server.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ layout: 'tabs' }} />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={setDefault}
          onChange={(e) => setSetDefault(e.target.checked)}
          className="size-4"
        />
        <span>Set as default card for future invoices</span>
      </label>
      {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
      <div className="flex justify-end gap-2 pt-1">
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? 'Saving…' : 'Save card'}
        </Button>
      </div>
    </form>
  );
}
